/**
 * ImagesCategoryPanel (Story 37.3 — Phase 2 category panel #1)
 *
 * The `images` sidebar category's floating-panel body. Replaces the generic
 * `CardListPanel` (which dropped a blank `image` widget the user then had to
 * configure) with a purpose-built **configure-then-add** picker: the user
 * uploads + configures an image FIRST, then Add attaches the populated config
 * to a new `image` widget so it renders non-blank (AC #1, AC #4).
 *
 * Reuse, don't fork: the upload + fit/align/alt/caption controls are the SHARED
 * `ImageConfigSection` from `widgetMediaForms` (the exact same sub-form the
 * `WidgetConfigPanel` uses when editing an existing image — AC #3 anti-drift).
 * The sub-form's contract is `{ widget, onUpdate }`; this panel has no real
 * widget yet, so it wraps a local **draft config** in a synthetic draft-widget
 * and adapts `onUpdate` to merge patches into the draft (see Dev Notes
 * "Adapting the sub-form prop contract" + Pre-mortem A).
 *
 * Add is DISABLED until `draft.imageUrl` is non-empty (an upload completed) —
 * the concrete "never blank" enforcement: there is no path from this panel to a
 * blank image widget.
 */

import { useState, useMemo, useCallback } from "react";
import { Image as ImageIcon } from "lucide-react";
import type { WidgetConfig } from "../../lib/templateUtils";
import { DEFAULT_WIDGET_CONFIGS } from "./canvasWidgetDefaults";
import { ImageConfigSection } from "./widgetMediaForms";
import type { CategoryPanelProps } from "./categoryPickerRegistry";

/** Synthetic draft-widget id — stable, never persisted (Pre-mortem A). */
const DRAFT_IMAGE_WIDGET_ID = "__draft_image__";

export function ImagesCategoryPanel({ onWidgetAdd }: CategoryPanelProps) {
  // Seed the draft from the real default config so a configured-then-added
  // image equals what a blank drop + manual fill would produce.
  const [draft, setDraft] = useState<Record<string, unknown>>(() => ({
    ...DEFAULT_WIDGET_CONFIGS.image,
  }));

  // Synthetic draft-widget wrapping the local draft. The sub-form only reads
  // `id` + `config`, but TS needs the full WidgetConfig shape, so fill the rest
  // with throwaway-but-valid defaults (never used by ImageConfigSection).
  const draftWidget = useMemo<WidgetConfig>(
    () => ({
      id: DRAFT_IMAGE_WIDGET_ID,
      type: "image" as const,
      title: "Image",
      position: { row: 0, col: 0, colSpan: 6, rowSpan: 3 },
      config: draft,
    }),
    [draft],
  );

  // Adapt the sub-form's `onUpdate(id, changes)` to merge `changes.config` into
  // the local draft (Pre-mortem A: the merge MUST land or Add stays disabled
  // forever). The id is ignored — there is only one draft.
  const handleDraftUpdate = useCallback(
    (_id: string, changes: Partial<WidgetConfig>) => {
      if (!changes.config) return;
      setDraft((d) => ({ ...d, ...(changes.config as Record<string, unknown>) }));
    },
    [],
  );

  const imageUrl = (draft.imageUrl as string) ?? "";
  // Add is enabled only once an upload completed (imageUrl set) AND a real add
  // handler is wired — a missing `onWidgetAdd` would otherwise leave a button
  // that wipes the draft and does nothing (code-review R1).
  const canAdd = imageUrl.length > 0 && typeof onWidgetAdd === "function";

  const handleAdd = useCallback(() => {
    if (!canAdd || !onWidgetAdd) return;
    const added = onWidgetAdd("image", { ...draft });
    // Reset the draft ONLY if the widget was actually added — a rejected add
    // (page at MAX_WIDGETS) must not discard the user's upload (code-review R1).
    if (added) setDraft({ ...DEFAULT_WIDGET_CONFIGS.image });
  }, [canAdd, onWidgetAdd, draft]);

  return (
    <div className="flex h-full flex-col p-3" data-testid="images-category-panel">
      <div className="mb-3 flex items-center gap-2">
        <ImageIcon size={18} className="text-muted-foreground" />
        <h3 className="text-[13px] font-semibold text-foreground">Images</h3>
      </div>

      <div className="flex-1 overflow-y-auto">
        <ImageConfigSection widget={draftWidget} onUpdate={handleDraftUpdate} />
      </div>

      <button
        type="button"
        data-testid="images-panel-add-btn"
        disabled={!canAdd}
        onClick={handleAdd}
        className="mt-3 w-full rounded-md border border-input bg-primary px-3 py-2 text-[13px] font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
      >
        Add to report
      </button>
    </div>
  );
}
